'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

import {
  googleAnalyticsAllowed,
  loadGoogleAnalytics,
  trackGoogleAnalyticsPageView,
} from '@/lib/google-analytics';

/**
 * Mounts Google Analytics 4 (gtag.js) only after the user has granted analytics
 * consent and when Global Privacy Control is not signalled. Reacts to consent
 * changes via the `storage` and `purebox:consent-changed` events and tracks
 * client-side route transitions across the Next.js App Router.
 */
export function GoogleAnalyticsConsentLoader() {
  const [enabled, setEnabled] = useState(false);
  const pathname = usePathname();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    const sync = () => setEnabled(googleAnalyticsAllowed());
    sync();
    window.addEventListener('storage', sync);
    window.addEventListener('purebox:consent-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('purebox:consent-changed', sync);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    loadGoogleAnalytics();
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !pathname) {
      return;
    }
    // The initial pageview is dispatched by gtag('config') inside loadGoogleAnalytics.
    // We track subsequent client-side route changes when the pathname changes.
    if (lastTrackedPath.current === null) {
      lastTrackedPath.current = pathname;
      return;
    }

    if (lastTrackedPath.current !== pathname) {
      lastTrackedPath.current = pathname;
      trackGoogleAnalyticsPageView(pathname);
    }
  }, [enabled, pathname]);

  return null;
}
